import { useEffect, useRef, useState } from "react";
import { Mic, Square, Volume2 } from "lucide-react";
import { aiAgentAPI } from "../../../services/apiService";
import { InlineLoader } from "../PageLoader";
import { NO_DATA } from "../../lib/format";
import { errorMessage } from "./kit";

/**
 * Voice agent test (old VoiceTestModal): record a question, POST /api/ai-agent/config/:id/test-voice
 * (multipart: audio, conversation_history, session_state) → { transcript, response, audio (base64 WAV),
 * conversation_messages, session_state }. The reply is played back; each turn carries the chain so the agent
 * remembers the call. Nothing is sent to a patient and no call is placed.
 * Tap to start, tap again to send (the old modal also stopped itself after 3 s of silence; this keeps it explicit).
 */
const MIN_BYTES = 500;

export function VoiceTest({ agent }) {
  const [turns, setTurns] = useState([]);
  const [state, setState] = useState("idle"); // idle | listening | processing | speaking
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef(null); const stream = useRef(null); const chunks = useRef([]); const timer = useRef(null); const audio = useRef(null);
  const history = useRef([]); const session = useRef(null); const closed = useRef(false);

  const stopMic = () => { stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null; window.clearInterval(timer.current); };
  // Set on mount as well: StrictMode runs the cleanup once in development, which would otherwise leave it closed.
  useEffect(() => { closed.current = false; return () => { closed.current = true; stopMic(); if (audio.current) audio.current.pause(); }; }, []);

  const send = async (blob) => {
    setState("processing"); setError("");
    const form = new FormData();
    form.append("audio", blob, "recording.webm");
    if (history.current.length) form.append("conversation_history", JSON.stringify(history.current));
    if (session.current) form.append("session_state", JSON.stringify(session.current));
    try {
      const res = await aiAgentAPI.testVoiceChat(agent.id, form);
      if (closed.current) return;
      if (!res.data?.success) { setError(res.data?.message || "The test couldn't be processed."); setState("idle"); return; }
      history.current = res.data.conversation_messages || [];
      session.current = res.data.session_state || null;
      setTurns((prev) => [...prev, { role: "user", text: res.data.transcript }, { role: "assistant", text: res.data.response }]);
      if (res.data.audio) play(res.data.audio); else setState("idle");
    } catch (err) {
      if (closed.current) return;
      const code = err?.response?.data?.error;
      setError(code === "unclear_speech" || code === "empty_transcript" ? "Olli couldn't make out any speech. Speak a little closer to the microphone and try again." : errorMessage(err, "The voice test failed. Try again."));
      setState("idle");
    }
  };

  const play = (base64) => {
    setState("speaking");
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: "audio/wav" }));
    const a = new Audio(url); audio.current = a;
    const done = () => { URL.revokeObjectURL(url); audio.current = null; if (!closed.current) setState("idle"); };
    a.onended = done;
    a.onerror = () => { done(); setError("The reply couldn't be played. Its text is shown above."); };
    a.play().catch(() => { done(); setError("The browser blocked playback. The reply's text is shown above."); });
  };

  const start = async () => {
    setError("");
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined") { setError("This browser can't record here. Use Chrome or Edge on https:// or localhost."); return; }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (closed.current) { s.getTracks().forEach((t) => t.stop()); return; }
      stream.current = s;
      const mimeType = window.MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const r = new window.MediaRecorder(s, { mimeType });
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      r.onstop = () => {
        if (closed.current) return;
        const blob = new Blob(chunks.current, { type: mimeType });
        if (blob.size > MIN_BYTES) send(blob); else { setError("No sound was recorded. Check the microphone and try again."); setState("idle"); }
      };
      recorder.current = r;
      r.start(250);
      setSeconds(0); timer.current = window.setInterval(() => setSeconds((n) => n + 1), 1000);
      setState("listening");
    } catch (_) {
      setError("Microphone access was refused. Allow the microphone for this site (the lock icon in the address bar), then try again.");
      setState("idle");
    }
  };
  const stop = () => { if (recorder.current?.state === "recording") recorder.current.stop(); stopMic(); };
  const reset = () => { history.current = []; session.current = null; setTurns([]); setError(""); };

  const busy = state === "processing" || state === "speaking";
  return <>
    <div className="mt-4 flex min-h-56 flex-col gap-3 rounded-sm bg-mist p-4">
      <p className="mr-auto max-w-[90%] rounded-lg rounded-tl-sm bg-paper px-3 py-2 text-small">{agent.greeting_message || NO_DATA}</p>
      {turns.map((t, i) => <p key={i} className={t.role === "user" ? "ml-auto max-w-[90%] rounded-lg rounded-tr-sm bg-sky-soft px-3 py-2 text-small" : "mr-auto max-w-[90%] rounded-lg rounded-tl-sm bg-paper px-3 py-2 text-small"}>{t.text || NO_DATA}</p>)}
      {state === "processing" && <div className="mr-auto flex items-center gap-2 rounded-lg rounded-tl-sm bg-paper px-3 py-2 text-small text-muted"><InlineLoader size={14} />Listening back and replying…</div>}
      {state === "speaking" && <p className="mr-auto flex items-center gap-2 text-caption text-muted"><Volume2 size={14} />Playing the reply</p>}
      {!turns.length && state === "idle" && <p className="mt-auto text-center text-caption text-muted">Tap the microphone, ask a question out loud, then tap again to hear the agent answer.</p>}
    </div>
    {error && <p role="alert" className="mt-2 rounded-sm bg-danger-soft px-3 py-2 text-caption text-danger">{error}</p>}
    <div className="mt-3 flex items-center gap-3">
      {state === "listening"
        ? <button type="button" onClick={stop} className="flex h-11 items-center gap-2 rounded-full bg-danger px-4 text-small font-semibold text-paper"><Square size={14} fill="currentColor" />Stop and send · {seconds}s</button>
        : <button type="button" onClick={start} disabled={busy} className="flex h-11 items-center gap-2 rounded-full bg-ink px-4 text-small font-semibold text-paper disabled:opacity-50"><Mic size={16} />{turns.length ? "Ask another question" : "Start speaking"}</button>}
      {turns.length > 0 && state === "idle" && <button type="button" onClick={reset} className="text-caption font-semibold text-muted hover:text-ink">Start over</button>}
    </div>
  </>;
}
